'use client';

import { useState, type ReactNode } from 'react';
import Link from 'next/link';
import { PlatformLogo } from '@/components/icons/platform-logo';
import type { MarketingLandingCopy } from '@/i18n/marketing-landing-copy';
import { cn } from '@/lib/utils';
import { MarketingLocaleSwitcher } from './marketing-locale-switcher';
import {
  DesktopNavDrawer,
  MobileNavDrawer,
  type MarketingMenu,
} from './landing-nav-drawers';
import { MarketingSquare } from './section-label';
import { SectionShell } from './section-shell';

const MENU_BY_HREF: Record<string, MarketingMenu> = {
  '#solutions': 'solutions',
  '#resources': 'resources',
};

/**
 * Both drawers stay modal for reliable focus and outside-click dismissal. The
 * positioned brand and nav remain above the desktop drawer; actions stay below.
 */
export function LandingHeader({ copy }: { copy: MarketingLandingCopy }) {
  const [openMenu, setOpenMenu] = useState<MarketingMenu | null>(null);
  const [portalContainer, setPortalContainer] = useState<HTMLElement | null>(
    null
  );

  return (
    <>
      {openMenu ? <div aria-hidden="true" className="h-18" /> : null}
      <header
        ref={setPortalContainer}
        className={cn(
          'bg-marketing-bg top-0 z-50 h-18 w-full',
          openMenu ? 'fixed inset-x-0' : 'sticky'
        )}
      >
        <SectionShell className="h-full py-4">
          <div className="pointer-events-auto relative grid grid-cols-3 items-start gap-4">
            <div className="col-start-1 w-full min-w-0">
              <Link
                href="/"
                onClick={() => setOpenMenu(null)}
                className="focus-visible:outline-marketing-accent-strong shrink-0 outline-none focus-visible:outline-2 focus-visible:outline-offset-4"
              >
                <span className="relative z-60 flex gap-1.5">
                  <PlatformLogo size={16} primaryColor="#000000" />
                  <span className="text-marketing-fg text-[clamp(0.875rem,1.1vw,1rem)] leading-tight font-semibold [letter-spacing:-0.02em] select-none">
                    {copy.brand.name}
                  </span>
                </span>
              </Link>
              <span className="text-marketing-fg mt-0.5 block text-xs leading-[1.45] whitespace-nowrap select-none lg:text-sm">
                {copy.brand.tagline}
              </span>
            </div>

            <nav
              aria-label={copy.brand.primaryNavLabel}
              className="tablet:flex relative z-70! col-start-2 row-start-1 hidden w-full items-center justify-center gap-6 pt-1"
            >
              {copy.primaryNav.map((link) => {
                const menu = MENU_BY_HREF[link.href];

                if (!menu) {
                  return (
                    <NavItemUnderline key={link.label}>
                      <Link
                        href={link.href}
                        onClick={() => setOpenMenu(null)}
                        className="text-marketing-fg hover:text-marketing-fg/80 focus-visible:outline-marketing-accent-strong text-sm font-medium [letter-spacing:-0.01em] transition-colors duration-200 outline-none focus-visible:outline-2 focus-visible:outline-offset-4"
                      >
                        {link.label}
                      </Link>
                    </NavItemUnderline>
                  );
                }

                return (
                  <NavItemUnderline key={link.label}>
                    <DesktopNavDrawer
                      closeLabel={copy.brand.closeMenuLabel}
                      groups={
                        menu === 'solutions'
                          ? copy.solutionsGroups
                          : [copy.resourcesGroup]
                      }
                      label={link.label}
                      layout={menu === 'solutions' ? 'columns' : 'centered'}
                      menu={menu}
                      onOpenMenuChange={setOpenMenu}
                      openMenu={openMenu}
                      portalContainer={portalContainer}
                    />
                  </NavItemUnderline>
                );
              })}
            </nav>

            <div className="tablet:flex col-start-3 row-start-1 hidden w-full items-center justify-end gap-1.5 pt-0.5 lg:gap-3">
              <MarketingLocaleSwitcher />
              <HeaderPill href={copy.brand.loginHref} tone="chip">
                {copy.brand.loginLabel}
              </HeaderPill>
              <HeaderPill href={copy.brand.ctaHref} tone="cta">
                {copy.brand.ctaLabel}
              </HeaderPill>
            </div>

            <div className="tablet:hidden col-start-3 row-start-1 justify-self-end">
              <MobileNavDrawer copy={copy} portalContainer={portalContainer} />
            </div>
          </div>
        </SectionShell>
      </header>
    </>
  );
}

function NavItemUnderline({ children }: { children: ReactNode }) {
  return (
    <div className="group relative">
      {children}
      <div className="bg-marketing-fg/60 absolute bottom-0 left-0 h-0.5 w-0 cursor-pointer transition-all duration-200 group-hover:w-full" />
    </div>
  );
}

function HeaderPill({
  children,
  href,
  tone,
}: {
  children: string;
  href: string;
  tone: 'chip' | 'cta';
}) {
  return (
    <Link
      href={href}
      className={cn(
        'group focus-visible:outline-marketing-accent-strong inline-flex items-center rounded-md px-3 py-2 text-xs font-medium [letter-spacing:-0.01em] outline-none focus-visible:outline-2 focus-visible:outline-offset-2 lg:px-4 lg:py-2 lg:text-sm',
        tone === 'chip' && 'bg-marketing-chip text-marketing-chip-fg',
        tone === 'cta' && 'bg-marketing-cta text-marketing-cta-fg'
      )}
    >
      <span className="relative inline-flex items-center gap-2">
        <MarketingSquare
          size="sm"
          className="bg-marketing-accent-strong absolute top-1/2 left-0 -translate-y-1/2 opacity-0 transition-opacity duration-200 ease-out group-hover:opacity-100 group-focus-visible:opacity-100 motion-reduce:transition-none"
        />
        <span className="transition-transform duration-200 ease-out group-hover:translate-x-4 group-focus-visible:translate-x-4 motion-reduce:transition-none">
          {children}
        </span>
        <MarketingSquare
          size="sm"
          className={cn(
            'transition-opacity duration-200 ease-out group-hover:opacity-0 group-focus-visible:opacity-0 motion-reduce:transition-none',
            tone === 'cta' && 'bg-marketing-cta-fg'
          )}
        />
      </span>
    </Link>
  );
}
